import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { moderatorAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { 
  Users, BookOpen, Calendar as CalendarIcon, LogOut, Loader2, Home, 
  Search, UserPlus, ChevronRight, AlertCircle, CheckCircle,
  Mail, Phone, Clock, GraduationCap, Plus, Eye, EyeOff, Globe
} from 'lucide-react';

const ModeratorDashboard = () => {
  const { language, toggleLanguage } = useLanguage();
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('students');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Enrollment modal state
  const [enrollModal, setEnrollModal] = useState({ open: false, student: null });
  const [selectedCourse, setSelectedCourse] = useState('');
  const [expiryDate, setExpiryDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Teacher creation modal state
  const [teacherModal, setTeacherModal] = useState(false);
  const [teacherForm, setTeacherForm] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    bio: '',
    bio_ar: '',
    intro_video_url: '',
    specializations: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const content = {
    en: {
      title: "Moderator Portal",
      welcome: "Welcome",
      students: "Students",
      enrollments: "Enrollments",
      overview: "Overview",
      totalStudents: "Total Students",
      activeEnrollments: "Active Enrollments",
      searchPlaceholder: "Search students by name or email...",
      enrollStudent: "Enroll Student",
      selectCourse: "Select Course",
      expiryDate: "Subscription Expiry Date",
      selectExpiryDate: "Select expiry date",
      enroll: "Enroll",
      cancel: "Cancel",
      noStudents: "No students found",
      noEnrollments: "No enrollments yet",
      viewDetails: "View Details",
      enrolledCourses: "Enrolled Courses",
      noEnrolledCourses: "No enrolled courses",
      enrollmentSuccess: "Student enrolled successfully!",
      logout: "Logout",
      backToHome: "Back to Home",
      status: "Status",
      active: "Active",
      expired: "Expired",
      expiresOn: "Expires",
      assignedBy: "Assigned by",
      // Teacher management
      teachers: "Teachers",
      addTeacher: "Add Teacher",
      teacherName: "Full Name",
      teacherEmail: "Email",
      teacherPassword: "Password",
      teacherPhone: "Phone (optional)",
      teacherBio: "Bio (English)",
      teacherBioAr: "Bio (Arabic)",
      teacherVideo: "Intro Video URL (optional)",
      teacherSpecializations: "Specializations (comma separated)",
      createTeacher: "Create Teacher",
      teacherSuccess: "Teacher created! Pending admin approval.",
      noTeachers: "No teachers found",
      pendingApproval: "Pending Approval",
      approved: "Approved",
      rejected: "Rejected"
    },
    ar: {
      title: "بوابة المشرف",
      welcome: "مرحباً",
      students: "الطلاب",
      enrollments: "التسجيلات",
      overview: "نظرة عامة",
      totalStudents: "إجمالي الطلاب",
      activeEnrollments: "التسجيلات النشطة",
      searchPlaceholder: "ابحث عن الطلاب بالاسم أو البريد الإلكتروني...",
      enrollStudent: "تسجيل طالب",
      selectCourse: "اختر الدورة",
      expiryDate: "تاريخ انتهاء الاشتراك",
      selectExpiryDate: "اختر تاريخ الانتهاء",
      enroll: "تسجيل",
      cancel: "إلغاء",
      noStudents: "لم يتم العثور على طلاب",
      noEnrollments: "لا توجد تسجيلات بعد",
      viewDetails: "عرض التفاصيل",
      enrolledCourses: "الدورات المسجلة",
      noEnrolledCourses: "لا توجد دورات مسجلة",
      enrollmentSuccess: "تم تسجيل الطالب بنجاح!",
      logout: "تسجيل الخروج",
      backToHome: "العودة للرئيسية",
      status: "الحالة",
      active: "نشط",
      expired: "منتهي",
      expiresOn: "ينتهي في",
      assignedBy: "تم التسجيل بواسطة",
      // Teacher management
      teachers: "المعلمين",
      addTeacher: "إضافة معلم",
      teacherName: "الاسم الكامل",
      teacherEmail: "البريد الإلكتروني",
      teacherPassword: "كلمة المرور",
      teacherPhone: "الهاتف (اختياري)",
      teacherBio: "السيرة الذاتية (بالإنجليزية)",
      teacherBioAr: "السيرة الذاتية (بالعربية)",
      teacherVideo: "رابط فيديو التعريف (اختياري)",
      teacherSpecializations: "التخصصات (مفصولة بفواصل)",
      createTeacher: "إنشاء معلم",
      teacherSuccess: "تم إنشاء المعلم! في انتظار موافقة المدير.",
      noTeachers: "لم يتم العثور على معلمين",
      pendingApproval: "في انتظار الموافقة",
      approved: "موافق عليه",
      rejected: "مرفوض"
    }
  };

  const t = content[language];

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (!authLoading && user && user.role !== 'moderator' && user.role !== 'admin') {
      navigate(`/${user.role}`);
      return;
    }

    const fetchData = async () => {
      try {
        const [dashboardRes, studentsRes, coursesRes, teachersRes] = await Promise.all([
          moderatorAPI.getDashboard(),
          moderatorAPI.getStudents(),
          moderatorAPI.getCourses(),
          moderatorAPI.getTeachers()
        ]);
        
        setDashboard(dashboardRes.data);
        setStudents(studentsRes.data.students || []);
        setCourses(coursesRes.data.courses || []);
        setTeachers(teachersRes.data.teachers || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (user && (user.role === 'moderator' || user.role === 'admin')) {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleEnroll = async () => {
    if (!selectedCourse || !enrollModal.student || !expiryDate) return;
    setSubmitting(true);
    setSuccessMessage('');
    
    try {
      await moderatorAPI.enrollStudent({
        student_id: enrollModal.student.id,
        course_id: selectedCourse,
        expiry_date: expiryDate.toISOString()
      });
      
      setSuccessMessage(t.enrollmentSuccess);
      
      // Refresh data
      const [dashboardRes, studentsRes] = await Promise.all([
        moderatorAPI.getDashboard(),
        moderatorAPI.getStudents()
      ]);
      setDashboard(dashboardRes.data);
      setStudents(studentsRes.data.students || []);
      
      // Close modal after short delay to show success message
      setTimeout(() => {
        setEnrollModal({ open: false, student: null });
        setSelectedCourse('');
        setExpiryDate(null);
        setSuccessMessage('');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to enroll student');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTeacher = async () => {
    if (!teacherForm.email || !teacherForm.password || !teacherForm.name) return;
    setSubmitting(true);
    setSuccessMessage('');
    
    try {
      const specializations = teacherForm.specializations
        ? teacherForm.specializations.split(',').map(s => s.trim()).filter(s => s)
        : [];
      
      await moderatorAPI.createTeacher({
        email: teacherForm.email,
        password: teacherForm.password,
        name: teacherForm.name,
        phone: teacherForm.phone || null,
        bio: teacherForm.bio || null,
        bio_ar: teacherForm.bio_ar || null,
        intro_video_url: teacherForm.intro_video_url || null,
        specializations
      });
      
      setSuccessMessage(t.teacherSuccess);
      
      // Refresh teachers list
      const teachersRes = await moderatorAPI.getTeachers();
      setTeachers(teachersRes.data.teachers || []);
      
      // Close modal and reset form
      setTimeout(() => {
        setTeacherModal(false);
        setTeacherForm({
          email: '',
          password: '',
          name: '',
          phone: '',
          bio: '',
          bio_ar: '',
          intro_video_url: '',
          specializations: ''
        });
        setSuccessMessage('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create teacher');
    } finally {
      setSubmitting(false);
    }
  };

  const getTeacherStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      pending: t.pendingApproval,
      approved: t.approved,
      rejected: t.rejected
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredStudents = students.filter(student => 
    student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTeachers = teachers.filter(teacher => 
    teacher.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => { setError(null); window.location.reload(); }}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
            <p className="text-gray-600">{t.welcome}, {user?.name}</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={toggleLanguage}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'العربية' : 'English'}</span>
            </Button>
            <Link to="/" className="text-gray-600 hover:text-teal-600 flex items-center gap-2">
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline">{t.backToHome}</span>
            </Link>
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t.logout}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <Users className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-3xl font-bold text-gray-900">{dashboard?.total_students || 0}</h3>
            <p className="text-gray-600">{t.totalStudents}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <CalendarIcon className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-3xl font-bold text-gray-900">{dashboard?.active_enrollments || 0}</h3>
            <p className="text-gray-600">{t.activeEnrollments}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <BookOpen className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-3xl font-bold text-gray-900">{courses.length}</h3>
            <p className="text-gray-600">Available Courses</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <GraduationCap className="w-8 h-8 text-amber-600 mb-3" />
            <h3 className="text-3xl font-bold text-gray-900">{teachers.length}</h3>
            <p className="text-gray-600">{t.teachers}</p>
          </div>
        </div>

        {/* Tabbed Content */}
        <div className="bg-white rounded-xl shadow-sm border">
          {/* Tabs */}
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => { setActiveTab('students'); setSearchQuery(''); }}
                className={`px-6 py-4 font-medium text-sm transition-colors ${
                  activeTab === 'students'
                    ? 'text-teal-600 border-b-2 border-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="students-tab"
              >
                <Users className="w-4 h-4 inline mr-2" />
                {t.students}
              </button>
              <button
                onClick={() => { setActiveTab('teachers'); setSearchQuery(''); }}
                className={`px-6 py-4 font-medium text-sm transition-colors ${
                  activeTab === 'teachers'
                    ? 'text-teal-600 border-b-2 border-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="teachers-tab"
              >
                <GraduationCap className="w-4 h-4 inline mr-2" />
                {t.teachers}
              </button>
            </div>
          </div>

          {/* Tab Header with Search and Action Button */}
          <div className="p-6 border-b">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1">
                <Search className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t.searchPlaceholder}
                  className={`${language === 'ar' ? 'pr-10' : 'pl-10'}`}
                />
              </div>
              {activeTab === 'teachers' && (
                <Button 
                  onClick={() => setTeacherModal(true)}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="add-teacher-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t.addTeacher}
                </Button>
              )}
            </div>
          </div>
          
          {/* Students Tab Content */}
          {activeTab === 'students' && (
            <div className="divide-y">
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student) => (
                  <div key={student.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{student.name}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {student.email}
                          </span>
                          {student.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {student.phone}
                            </span>
                          )}
                        </div>
                        {student.enrollments?.length > 0 && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">{t.enrolledCourses}:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {student.enrollments.map((enrollment) => (
                                <span 
                                  key={enrollment.id}
                                  className={`text-xs px-2 py-1 rounded-full ${
                                    new Date(enrollment.subscription_end) > new Date()
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {enrollment.course_id}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <Button 
                        onClick={() => setEnrollModal({ open: true, student })}
                        className="bg-teal-600 hover:bg-teal-700"
                        data-testid={`enroll-btn-${student.id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        {t.enrollStudent}
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.noStudents}</p>
                </div>
              )}
            </div>
          )}

          {/* Teachers Tab Content */}
          {activeTab === 'teachers' && (
            <div className="divide-y">
              {filteredTeachers.length > 0 ? (
                filteredTeachers.map((teacher) => (
                  <div key={teacher.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{teacher.name}</h3>
                          {getTeacherStatusBadge(teacher.teacher_status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {teacher.email}
                          </span>
                          {teacher.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {teacher.phone}
                            </span>
                          )}
                        </div>
                        {teacher.bio && (
                          <p className="text-sm text-gray-500 mb-2 line-clamp-2">{teacher.bio}</p>
                        )}
                        {teacher.profile?.specializations?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {teacher.profile.specializations.map((spec, idx) => (
                              <span key={idx} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                                {spec}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.noTeachers}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Enrollment Modal */}
      <Dialog open={enrollModal.open} onOpenChange={(open) => !open && setEnrollModal({ open: false, student: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.enrollStudent}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {successMessage && (
              <div className="bg-green-50 text-green-700 p-3 rounded-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {successMessage}
              </div>
            )}
            
            <div>
              <Label>Student</Label>
              <p className="text-sm font-medium text-gray-900">{enrollModal.student?.name}</p>
              <p className="text-sm text-gray-500">{enrollModal.student?.email}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="course">{t.selectCourse}</Label>
              <select
                id="course"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">{t.selectCourse}...</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title || course.title_ar || course.id} {course.teacher && `- ${course.teacher.name}`}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>{t.expiryDate}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-normal ${!expiryDate && 'text-muted-foreground'}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {expiryDate ? format(expiryDate, 'PPP') : t.selectExpiryDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={expiryDate}
                    onSelect={setExpiryDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollModal({ open: false, student: null })}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleEnroll} 
              disabled={submitting || !selectedCourse || !expiryDate}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.enroll}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teacher Creation Modal */}
      <Dialog open={teacherModal} onOpenChange={setTeacherModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-teal-600" />
              {t.addTeacher}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {successMessage && (
              <div className="bg-green-50 text-green-700 p-3 rounded-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {successMessage}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="teacherName">{t.teacherName} *</Label>
                <Input
                  id="teacherName"
                  value={teacherForm.name}
                  onChange={(e) => setTeacherForm({...teacherForm, name: e.target.value})}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="teacherPhone">{t.teacherPhone}</Label>
                <Input
                  id="teacherPhone"
                  value={teacherForm.phone}
                  onChange={(e) => setTeacherForm({...teacherForm, phone: e.target.value})}
                  placeholder="+1234567890"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="teacherEmail">{t.teacherEmail} *</Label>
              <Input
                id="teacherEmail"
                type="email"
                value={teacherForm.email}
                onChange={(e) => setTeacherForm({...teacherForm, email: e.target.value})}
                placeholder="teacher@example.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="teacherPassword">{t.teacherPassword} *</Label>
              <div className="relative">
                <Input
                  id="teacherPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={teacherForm.password}
                  onChange={(e) => setTeacherForm({...teacherForm, password: e.target.value})}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${language === 'ar' ? 'left-3' : 'right-3'}`}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="teacherBio">{t.teacherBio}</Label>
              <Textarea
                id="teacherBio"
                value={teacherForm.bio}
                onChange={(e) => setTeacherForm({...teacherForm, bio: e.target.value})}
                placeholder="Experienced English teacher..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="teacherBioAr">{t.teacherBioAr}</Label>
              <Textarea
                id="teacherBioAr"
                value={teacherForm.bio_ar}
                onChange={(e) => setTeacherForm({...teacherForm, bio_ar: e.target.value})}
                placeholder="مدرس لغة انجليزية..."
                rows={2}
                dir="rtl"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="teacherVideo">{t.teacherVideo}</Label>
              <Input
                id="teacherVideo"
                value={teacherForm.intro_video_url}
                onChange={(e) => setTeacherForm({...teacherForm, intro_video_url: e.target.value})}
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="teacherSpecs">{t.teacherSpecializations}</Label>
              <Input
                id="teacherSpecs"
                value={teacherForm.specializations}
                onChange={(e) => setTeacherForm({...teacherForm, specializations: e.target.value})}
                placeholder="IELTS, Business English, Kids English"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeacherModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleCreateTeacher} 
              disabled={submitting || !teacherForm.email || !teacherForm.password || !teacherForm.name}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.createTeacher}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModeratorDashboard;
